import React from 'react';
import styled, { css } from 'styled-components';
import { InputText } from 'primereact/inputtext';
import WizardAddButton from '../epd-wizard-inputs/WizardAddButton';
import { StyledCrossIcon, StyledDownloadIcon } from '../epd-compiler-inputs/common-icons';
import Tooltip from 'components/controls/Tooltip';

interface Props {
  name?: string;
  value?: string;
  placeholder: string;
  addButtonText: string;
  addButtonDisabled?: boolean;
  onOpenSelector(): void;
  disabled?: boolean;
  showDeleteButton?: boolean;
  onDeleteClicked?(): void;
  showDownloadButton?: boolean;
  onDownloadClicked?(): void;
  downloadButtonDisabled?: boolean;
}

const SelectorButton: React.FC<Props> = ({
  name,
  value,
  placeholder,
  addButtonText,
  addButtonDisabled,
  onOpenSelector,
  disabled,
  showDeleteButton,
  onDeleteClicked,
  showDownloadButton,
  onDownloadClicked,
  downloadButtonDisabled,
}) => {
  const buttons = [
    showDeleteButton && <StyledCrossIcon onClick={onDeleteClicked} />,
    showDownloadButton && (
      <>
        <StyledDownloadIcon onClick={onDownloadClicked} $disabled={downloadButtonDisabled} />
        {downloadButtonDisabled && (
          <Tooltip position="bottom" offset={12}>
            No file to download
          </Tooltip>
        )}
      </>
    ),
  ].filter(React.isValidElement);

  return (
    <div>
      <InputGroup className="p-inputgroup" $disabled={disabled}>
        <ClickArea onClick={onOpenSelector} disabled={disabled}>
          <InputTextStyled
            name={name}
            value={value}
            placeholder={placeholder}
            style={{ pointerEvents: 'none' }}
            tabIndex={-1}
            disabled={disabled}
            readOnly
          />
        </ClickArea>
        {buttons.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {buttons.map((b, i) => (
              <div key={i} style={{ marginRight: 12, display: 'flex' }}>
                {b}
              </div>
            ))}
          </div>
        )}
      </InputGroup>
      <WizardAddButton label={addButtonText} onClick={onOpenSelector} disabled={addButtonDisabled} />
    </div>
  );
};

export default SelectorButton;

const ClickArea = styled.button`
  all: unset;
  display: flex;
  width: 100%;
  ${(props) =>
    !props.disabled &&
    css`
      cursor: pointer;
    `}
`;

export const InputTextStyled = styled(InputText)`
  padding: 1rem;
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  border-radius: 2px;
  margin: 0;
  background-color: ${(props) => props.theme.colors.primaryWhite};
  ${(props) => props.theme.typography.bodyInput};
  color: ${(props) => props.theme.colors.primaryBlack};

  &.p-component.p-disabled {
    background-color: ${(props) => props.theme.colors.elementsFilledInput};
    color: ${(props) => props.theme.colors.primaryBlack70};
  }

  &:focus {
    border-color: ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &:hover {
    border-color: ${(props) => (props.disabled ? props.theme.colors.elementsInputBorder : props.theme.colors.primaryOrange)};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.primaryBlack50};
  }

  height: 44px;
`;

const InputGroup = styled.div<{ $disabled: boolean | undefined }>`
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  border-radius: 2px;
  ${(props) =>
    props.$disabled &&
    css`
      background-color: ${props.theme.colors.elementsFilledInput};
      border: none;
    `}
  &:has(${ClickArea}:focus-visible) {
    border-color: ${(props) => props.theme.colors.primaryOrange};
  }
  .p-inputgroup-addon:last-child {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  }
  ${InputTextStyled} {
    border: none;
    cursor: auto;
    &:focus {
      border: none;
      box-shadow: none;
    }
  }
`;
